import React from 'react';

/**
 * Main section card with white background, shadow, and title
 */
interface SectionCardProps {
  title: string;
  /** Rendered on the right of the title bar (e.g. a status checkbox). */
  action?: React.ReactNode;
  children: React.ReactNode;
}

export const SectionCard: React.FC<SectionCardProps> = ({ title, action, children }) => (
  <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
    <div className="flex items-center justify-between gap-4 mb-4 pb-4 border-b border-gray-200 dark:border-white/10">
      <h6 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h6>
      {action}
    </div>
    {children}
  </div>
);

/**
 * Subsection card - slightly lighter styling for nested sections
 */
interface SubSectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

export const SubSection: React.FC<SubSectionProps> = ({ title, children, className = '' }) => (
  <div className={`bg-gray-50 dark:bg-white/5 rounded-lg p-4 ${className}`}>
    <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{title}</h5>
    {children}
  </div>
);

/**
 * Item card (for grid items like MLS entries, testimonials, team members)
 */
interface ItemCardProps {
  children: React.ReactNode;
  className?: string;
}

export const ItemCard: React.FC<ItemCardProps> = ({ children, className = '' }) => (
  <div className={`bg-white dark:bg-white/5 p-3 rounded-lg border border-gray-300 dark:border-white/15 ${className}`}>{children}</div>
);

/**
 * Grid container for DataFields
 */
interface FieldGridProps {
  children: React.ReactNode;
  cols?: 1 | 2 | 3 | 4;
}

export const FieldGrid: React.FC<FieldGridProps> = ({ children, cols = 2 }) => {
  const colsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4',
  }[cols];

  return <div className={`grid ${colsClass} gap-x-6 gap-y-3`}>{children}</div>;
};

/**
 * Grid for item cards
 */
interface ItemGridProps {
  children: React.ReactNode;
  cols?: 2 | 3;
}

export const ItemGrid: React.FC<ItemGridProps> = ({ children, cols = 2 }) => {
  const colsClass =
    cols === 3 ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2';
  return <div className={`grid ${colsClass} gap-3`}>{children}</div>;
};

/**
 * View link for images/headshots
 */
interface ViewLinkProps {
  href: string;
  label?: string;
}

export const ViewLink: React.FC<ViewLinkProps> = ({ href, label = 'View Image' }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-yellow-600 hover:text-yellow-700 text-xs hover:underline"
  >
    {label}
  </a>
);

/**
 * Tag list for emails
 */
interface TagListProps {
  items: string[];
}

export const TagList: React.FC<TagListProps> = ({ items }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((item, index) => (
      <span key={index} className="px-2 py-1 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 rounded text-sm">
        {item}
      </span>
    ))}
  </div>
);

/**
 * Stat card for hero stats
 */
interface StatCardProps {
  value: string;
  label: string;
}

export const StatCard: React.FC<StatCardProps> = ({ value, label }) => (
  <div className="bg-white dark:bg-white/5 p-2 rounded-lg text-center border border-gray-300 dark:border-white/15">
    <div className="font-bold text-gray-900 dark:text-gray-100">{value || '-'}</div>
    <div className="text-xs text-gray-500 dark:text-gray-400">{label || '-'}</div>
  </div>
);

/**
 * Social link button
 */
interface SocialLinkProps {
  platform: string;
  url: string;
}

export const SocialLink: React.FC<SocialLinkProps> = ({ platform, url }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="px-3 py-1 bg-yellow-50 dark:bg-yellow-900/20 rounded text-yellow-600 dark:text-yellow-300 hover:text-yellow-700 dark:hover:text-yellow-200 text-sm hover:underline"
  >
    {platform}
  </a>
);

/**
 * Text block for bio/long text
 */
interface TextBlockProps {
  label: string;
  text: string;
}

export const TextBlock: React.FC<TextBlockProps> = ({ label, text }) => (
  <div className="mt-3">
    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-gray-50 dark:bg-white/5 p-3 rounded">{text}</p>
  </div>
);

/**
 * Label for subsection groups
 */
interface SectionLabelProps {
  children: React.ReactNode;
}

export const SectionLabel: React.FC<SectionLabelProps> = ({ children }) => (
  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase">{children}</p>
);

/**
 * Info note - for displaying webhook/credential notes
 */
interface InfoNoteProps {
  children: React.ReactNode;
}

export const InfoNote: React.FC<InfoNoteProps> = ({ children }) => (
  <p className="text-xs text-gray-400 dark:text-gray-500 italic mt-2">{children}</p>
);
